import { gql, useMutation, useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';

interface PushSubscriptionData {
    id: string;
    endpoint: string;
    createdAt: string;
    recordId: string | null;
    documentType: string | null;
}

const SUBSCRIBE_PUSH = gql`
    mutation SubscribePush(
        $endpoint: String!
        $p256dh: String!
        $auth: String!
        $recordId: UUID
        $documentType: String
    ) {
        subscribePush(
            endpoint: $endpoint
            p256dh: $p256dh
            auth: $auth
            recordId: $recordId
            documentType: $documentType
        ) {
            success
        }
    }
`;

const UNSUBSCRIBE_PUSH = gql`
    mutation UnsubscribePush($endpoint: String!, $recordId: UUID) {
        unsubscribePush(endpoint: $endpoint, recordId: $recordId) {
            success
        }
    }
`;

const GET_PUSH_SUBSCRIPTIONS = gql`
    query GetPushSubscriptions {
        pushSubscriptions {
            id
            endpoint
            createdAt
            recordId
            documentType
        }
    }
`;

const VAPID_PUBLIC_KEY = process.env.VAPID_PUBLIC_KEY || '';

export const usePushNotifications = ({ recordId, documentType }: { recordId?: string; documentType?: string } = {}) => {
    const [subscribePush] = useMutation(SUBSCRIBE_PUSH);
    const [unsubscribePush] = useMutation(UNSUBSCRIBE_PUSH);
    const { data, refetch } = useQuery(GET_PUSH_SUBSCRIPTIONS);
    const [isSupported, setIsSupported] = useState(false);
    const [browserEndpoint, setBrowserEndpoint] = useState<string | null>(null);

    useEffect(() => {
        const nextIsSupported = 'serviceWorker' in navigator && 'PushManager' in window;

        setIsSupported(nextIsSupported);

        // Get current browser subscription endpoint
        if (nextIsSupported) {
            navigator.serviceWorker.ready.then((registration) => {
                registration.pushManager.getSubscription().then((sub) => {
                    setBrowserEndpoint(sub?.endpoint || null);
                });
            });
        }
    }, []);

    const allSubscriptions: PushSubscriptionData[] = data?.pushSubscriptions || [];

    const subscriptions = allSubscriptions.filter((sub: PushSubscriptionData) => {
        const matchesRecordId = !recordId || sub.recordId === recordId;
        const matchesDocumentType = !documentType || sub.documentType === documentType;
        return matchesRecordId && matchesDocumentType;
    });

    // Check if the current browser endpoint has a matching subscription in the backend
    const isSubscribed = browserEndpoint !== null && subscriptions.some((sub) => sub.endpoint === browserEndpoint);

    const subscribe = async (options: { recordId?: string; documentType?: string } = {}) => {
        const registration = await navigator.serviceWorker.ready;

        const sub = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
        });

        await subscribePush({
            variables: {
                endpoint: sub.endpoint,
                p256dh: arrayBufferToBase64(sub.getKey('p256dh')),
                auth: arrayBufferToBase64(sub.getKey('auth')),
                recordId: options.recordId ?? recordId,
                documentType: options.documentType ?? documentType,
            },
        });

        setBrowserEndpoint(sub.endpoint);
        await refetch();
    };

    const unsubscribe = async (options: { recordId?: string } = {}) => {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();

        if (subscription) {
            await subscription.unsubscribe();
            await unsubscribePush({
                variables: {
                    endpoint: subscription.endpoint,
                    recordId: options.recordId ?? recordId,
                },
            });

            setBrowserEndpoint(null);
            await refetch();
        }
    };

    return {
        isSupported: Boolean(isSupported && VAPID_PUBLIC_KEY),
        isSubscribed,
        subscribe,
        unsubscribe,
        subscriptions,
    };
};

const urlBase64ToUint8Array = (base64String: string) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    return new Uint8Array([...rawData].map((char) => char.charCodeAt(0)));
};

const arrayBufferToBase64 = (buffer: ArrayBuffer | null) => {
    if (!buffer) return '';
    const bytes = new Uint8Array(buffer);
    let binary = '';
    bytes.forEach((b) => (binary += String.fromCharCode(b)));
    return window.btoa(binary);
};
